/**
 * NameCell Component
 *
 * Displays a name with optional metadata in a two-line layout.
 * Used in the SelectionTable for showing vulnerability/asset names.
 */

import { OverflowText } from '@/components/OverflowText';

/**
 * Props for NameCell component
 */
export interface NameCellProps {
  /** Primary display name */
  name: string;
  /** Secondary metadata text (optional) */
  metadata?: string;
}

/**
 * NameCell displays a name with optional metadata in a two-line layout.
 *
 * Layout:
 * - Primary name: Bold, brand-primary color, truncates with tooltip
 * - Metadata: Smaller text, secondary color, truncates with tooltip
 *
 * @example
 * ```tsx
 * <NameCell name="CVE-2024-12345" metadata="example.com" />
 * ```
 */
export function NameCell({ name, metadata }: NameCellProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <OverflowText
        className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary truncate font-semibold"
        text={name}
      />
      {metadata && (
        <OverflowText
          className="text-chariot-text-secondary group-[.is-selected]:text-chariot-text-primary truncate text-xs"
          text={metadata}
        />
      )}
    </div>
  );
}
